import { useState } from 'react'
import ComponentModal from './ComponentModal'
import { chatMode, isShowChatRoom } from '../../../../functions/chat'
import { useChatStore } from '../../../../stores/ChatStore'
import Swal from 'sweetalert2'
import { postForwardChat, postForwardGroupChat } from '../../../../api/ChatCRUD'
import useAccessToken from '../../../../hooks/AccessToken'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { ChatGroupRoom, ChatRoom } from '../../../../interfaces/Chat/Chat'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { useSettingsStore } from '../../../../stores/SettingsStore'
import useUser from '../../../../hooks/User'
import { useIntl } from 'react-intl'
import useHandleCheckSessionChat from '../../../../hooks/HandleCheckSessionChat'

interface ModalForwardChatProps {
    isOpen: boolean
    onClose: () => void
}

const ModalForwardChat = ({ isOpen, onClose }: ModalForwardChatProps) => {
    const token = useAccessToken()
    const user = useUser()
    const intl = useIntl()
    const { settings } = useSettingsStore()
    const { handleCheckSession } = useHandleCheckSessionChat();
    const {
        selectedChatsForward, selectedChatPhone, listChatAgents, listChatRooms, groupID,
        setSelectedChatsForward, setSelectedForwardMessages, tokenChat, forwardChatPayload,
        setForwardChatPayload, setIsOpenCheckBoxForward, phoneNumber, listChatGroupRooms,
        selectedChatGroupsForward, setSelectedChatGroupsForward, setForwardChatGroupPayload,
        forwardChatGroupPayload, setSelectedForwardGroupMessages, setIsOpenCheckBoxGroupForward
    } = useChatStore()
    const [searchQuery, setSearchQuery] = useState('')
    const [isForwarding, setIsForwarding] = useState(false)

    const handleForwardChat = async (targets: string[], rawMessages: string[], type: "chat" | "chat-group") => {
        try {
            let responseChat = null;
            let responseGroupChat = null;
            const asPhone = phoneNumber.split("-")[1] || groupID.split("|")[1];

            if (type === "chat" && targets.length > 0)
                responseChat = await postForwardChat(
                    targets,
                    rawMessages,
                    asPhone,
                    chatMode(settings.chat_version),
                    token,
                    tokenChat
                );

            if (type === "chat-group" && targets.length > 0)
                responseGroupChat = await postForwardGroupChat(
                    targets,
                    rawMessages,
                    asPhone,
                    chatMode(settings.chat_version),
                    token,
                    tokenChat
                );

            return responseChat?.data.success || responseGroupChat?.data.success;
        } catch (error: any) {
            const funct = () => handleForwardChat(targets, rawMessages, type);
            handleCheckSession('forwardChat', funct);
        }
    };

    const handleSelectChat = (chat: ChatRoom) => {
        if (selectedChatsForward.includes(chat.roomPhone)) {
            setSelectedChatsForward(selectedChatsForward.filter(phone => phone !== chat.roomPhone))
            setForwardChatPayload({
                ...forwardChatPayload,
                targets: forwardChatPayload.targets?.filter(target => target !== chat.roomPhone) || [],
            })
        } else {
            setSelectedChatsForward([...selectedChatsForward, chat.roomPhone])
            setForwardChatPayload({
                ...forwardChatPayload,
                targets: [...(forwardChatPayload.targets || []), chat.roomPhone],
            })
        }
    }

    const handleSelectGroupChat = (chat: ChatGroupRoom) => {
        if (selectedChatGroupsForward.includes(chat.groupId)) {
            setSelectedChatGroupsForward(selectedChatGroupsForward.filter(id => id !== chat.groupId))
            setForwardChatGroupPayload({
                ...forwardChatGroupPayload,
                targets: forwardChatGroupPayload.targets?.filter(target => target !== chat.groupId) || [],
            })
        } else {
            setSelectedChatGroupsForward([...selectedChatGroupsForward, chat.groupId])
            setForwardChatGroupPayload({
                ...forwardChatGroupPayload,
                targets: [...(forwardChatGroupPayload.targets || []), chat.groupId],
            })
        }
    }

    const handleForwardMessages = async () => {
        if (selectedChatsForward.length === 0 && selectedChatGroupsForward.length === 0) return;
        setIsForwarding(true);

        const newForwardChatPayload = forwardChatPayload.rawMessages?.sort((a, b) => new Date(a.date_created).getTime() - new Date(b.date_created).getTime());
        const newForwardChatGroupPayload = forwardChatGroupPayload.rawMessages?.sort((a, b) => new Date(a.date_created).getTime() - new Date(b.date_created).getTime());

        const newForwardChat = (newForwardChatPayload && newForwardChatPayload.length > 0) ? newForwardChatPayload.map((msg) => msg.id) : newForwardChatGroupPayload?.map((msg) => msg.id);
        const newForwardChatGroup = (newForwardChatGroupPayload && newForwardChatGroupPayload.length > 0) ? newForwardChatGroupPayload.map((msg) => msg.id) : newForwardChatPayload?.map((msg) => msg.id);

        onClose();
        setIsOpenCheckBoxForward(false);
        setIsOpenCheckBoxGroupForward(false);

        let success = false;

        if (forwardChatPayload.targets.length > 0) {
            const result = await handleForwardChat(selectedChatsForward, newForwardChat, "chat");
            if (result) success = true;
        }

        if (forwardChatGroupPayload.targets.length > 0) {
            const result = await handleForwardChat(selectedChatGroupsForward, newForwardChatGroup, "chat-group");
            if (result) success = true;
        }

        if (success) {
            setSelectedChatsForward([]);
            setSelectedChatGroupsForward([]);
            setSelectedForwardMessages([]);
            setSelectedForwardGroupMessages([]);
            setForwardChatPayload({ targets: [], rawMessages: [] });
            setForwardChatGroupPayload({ targets: [], rawMessages: [] });
            setSearchQuery("");
            setIsForwarding(false);

            Swal.fire({
                icon: "success",
                title: intl.formatMessage({ id: "ALERT.TITLE.MESSAGES_FORWARDED" }),
                text: intl.formatMessage({ id: "ALERT.TEXT.MESSAGES_FORWARDED" }),
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
            });
        }
    };

    const filteredChats = Object.values(listChatRooms).filter(chat =>
        chat.as === (phoneNumber.split('-')[1] || groupID.split('|')[1]) &&
        (chat.roomName?.toLowerCase().includes(searchQuery?.toLowerCase()) || chat.roomPhone.includes(searchQuery))
    )

    const filteredChatGroups = Object.values(listChatGroupRooms).filter(chat =>
        chat.as === (groupID.split('|')[1] || phoneNumber.split('-')[1]) &&
        (chat.groupName?.toLowerCase().includes(searchQuery?.toLowerCase()))
    )

    if (!isOpen) return null

    return (
        <ComponentModal>
            <div className='modal-header border-bottom'>
                <h5 className='modal-title fw-bold d-flex gap-2'>
                    {
                        selectedChatPhone.length > 1 &&
                        <OverlayTrigger
                            placement='bottom'
                            overlay={
                                <Tooltip id='tooltip-labels'>
                                    <p><b>{phoneNumber.split('-')[1]}</b></p>
                                    <span>{selectedChatPhone.find(item => item.phone === phoneNumber.split('-')[1])?.ownersName.join(', ')}</span>
                                </Tooltip>}
                        >
                            <div className="d-flex align-items-center">
                                <span className="bullet bullet-dot h-10px w-10px" style={{ backgroundColor: selectedChatPhone.find(item => item.phone === phoneNumber.split('-')[1])?.color }}></span>
                            </div>
                        </OverlayTrigger>
                    }

                    Forward Chat
                </h5>
            </div>

            {/* Search Input */}
            <div className='p-4 border-bottom'>
                <div className='position-relative'>
                    <i className='bi bi-search position-absolute top-50 translate-middle-y ms-3 text-gray-600'></i>
                    <input
                        type='text'
                        className='form-control form-control-solid ps-10'
                        placeholder='Search'
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            <div className='modal-body' style={{ maxHeight: '350px', overflowY: 'auto' }}>
                {filteredChats.map((chat: ChatRoom) => (
                    <div
                        key={chat.roomPhone}
                        className={`${isShowChatRoom(settings, listChatAgents, listChatRooms[`${chat.roomPhone}-${chat.as}`], user) ? 'd-flex ' : 'd-none'} align-items-center gap-3 p-3 border-bottom cursor-pointer hover-bg-light ${selectedChatsForward.includes(chat.roomPhone) ? 'bg-light-primary' : ''}`}
                        onClick={() => handleSelectChat(chat)}
                    >
                        <div className="form-check form-check-custom form-check-solid" onClick={(e) => e.stopPropagation()}>
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={selectedChatsForward.includes(chat.roomPhone)}
                                onChange={() => handleSelectChat(chat)}
                            />
                        </div>

                        <div className='symbol symbol-45px symbol-circle'>
                            <div className='symbol symbol-45px symbol-circle'>
                                <span className='symbol-label bg-light-danger text-danger fs-6 fw-bolder'>
                                    <img src={toAbsoluteUrl('/media/logos/logo-wooblazz.png')} className='symbol symbol-45px symbol-circle img-fluid' style={{ padding: "10px" }} alt='profile' />
                                </span>
                            </div>
                        </div>
                        <div className='d-flex flex-column flex-grow-1'>
                            <span className='fs-5 fw-bold text-dark'>{chat.roomName}</span>
                            <span className='fs-7 text-gray-600'>{chat.roomPhone}</span>
                        </div>
                        {selectedChatsForward.includes(chat.roomPhone) && (
                            <i className='bi bi-check-circle-fill text-primary fs-4'></i>
                        )}
                    </div>
                ))}

                {filteredChatGroups.map((chat: ChatGroupRoom) => (
                    <div
                        key={chat.groupId}
                        className={`d-flex align-items-center gap-3 p-3 border-bottom cursor-pointer hover-bg-light ${selectedChatGroupsForward.includes(chat.groupId) ? 'bg-light-primary' : ''}`}
                        onClick={() => handleSelectGroupChat(chat)}
                    >
                        <div className="form-check form-check-custom form-check-solid" onClick={(e) => e.stopPropagation()}>
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={selectedChatGroupsForward.includes(chat.groupId)}
                                onChange={() => handleSelectGroupChat(chat)}
                            />
                        </div>

                        <div className='symbol symbol-45px symbol-circle'>
                            <div className='symbol symbol-45px symbol-circle'>
                                <span className='symbol-label bg-light-danger text-danger fs-6 fw-bolder'>
                                    <img src={toAbsoluteUrl('/media/logos/logo-wooblazz.png')} className='symbol symbol-45px symbol-circle img-fluid' style={{ padding: "10px" }} alt='profile' />
                                </span>
                            </div>
                        </div>
                        <div className='d-flex flex-column flex-grow-1'>
                            <span className='fs-5 fw-bold text-dark'>{chat.groupName}</span>
                            <span className='fs-7 text-gray-600'>{chat.participants.length} {intl.formatMessage({ id: "FORM.LABEL.MEMBER" })}</span>
                        </div>
                        {selectedChatGroupsForward.includes(chat.groupId) && (
                            <i className='bi bi-check-circle-fill text-primary fs-4'></i>
                        )}
                    </div>
                ))}
            </div>

            <div className='modal-footer border-top'>
                <button
                    type='button'
                    className='btn btn-light'
                    onClick={() => {
                        onClose()
                        setSearchQuery('')
                    }}
                    disabled={isForwarding}
                >
                    Cancel
                </button>
                <button
                    type='button'
                    className='btn btn-primary'
                    disabled={(selectedChatsForward.length + selectedChatGroupsForward.length) === 0 || isForwarding}
                    onClick={handleForwardMessages}
                >
                    <i className='fa fa-paper-plane me-2'></i>
                    Forward ({selectedChatsForward.length + selectedChatGroupsForward.length})
                </button>
            </div>
        </ComponentModal>
    )
}

export default ModalForwardChat